import React, { useState } from 'react';
import { FileText, Sparkles, AlertCircle} from 'lucide-react';

const InstructionsPage = () => {
  const [instructionTab, setInstructionTab] = useState('quickbooks');

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        {/* Header with icon */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl">
            <FileText className="w-8 h-8 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Complete User Guide</h2>
            <p className="text-gray-600">Step-by-step instructions for QuickBooks & Coupa</p>
          </div>
        </div>

        {/* System Tabs */}
        <div className="mb-8">
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setInstructionTab('quickbooks')}
              className={`px-6 py-3 font-medium transition-all relative ${
                instructionTab === 'quickbooks'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <span>QuickBooks Online</span>
                {instructionTab === 'quickbooks' && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">
                    POPULAR
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setInstructionTab('coupa')}
              className={`px-6 py-3 font-medium transition-all relative ${
                instructionTab === 'coupa'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Coupa
            </button>
          </div>
        </div>

        {/* QuickBooks Instructions */}
        {instructionTab === 'quickbooks' && (
          <>
            {/* Legal Disclaimer Banner */}
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <p className="text-xs text-blue-900 leading-relaxed">
                <strong>⚖️ Legal Notice:</strong> This application is an independent third-party tool and is not affiliated with, endorsed by, or sponsored by Intuit Inc. "QuickBooks" is a registered trademark of Intuit Inc. This tool generates data files in formats compatible with QuickBooks import specifications for user convenience only. All trademarks are the property of their respective owners.
              </p>
            </div>

            {/* Progress Steps for QuickBooks */}
            <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div className="flex items-center justify-between">
                {['Upload', 'Review', 'Edit Items', 'Export', 'Import to QuickBooks'].map((step, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold mb-2">
                        {idx + 1}
                      </div>
                      <span className="text-xs font-medium text-gray-700 text-center">{step}</span>
                    </div>
                    {idx < 4 && (
                      <div className="w-12 h-0.5 bg-green-300 mx-2 mt-[-20px]"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              {/* Step 1: Upload */}
              <section className="border-l-4 border-green-600 pl-6">
                <h3 className="text-xl font-semibold text-green-600 mb-3 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  Upload Your Invoice
                </h3>
                <p className="text-gray-700 mb-3">
                  Start by uploading a PDF invoice. AI will automatically extract all data.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Click "Choose PDF File" button</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>Wait 10-30 seconds for AI processing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span><strong>Uses 1 credit per invoice</strong></span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Step 2: Review */}
              <section className="border-l-4 border-emerald-600 pl-6">
                <h3 className="text-xl font-semibold text-emerald-600 mb-3 flex items-center gap-2">
                  <span className="bg-emerald-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Review Extracted Data
                </h3>
                <p className="text-gray-700 mb-3">
                  AI extracts customer info, line items, prices, and more. Review and edit as needed.
                </p>
              </section>

              {/* Step 3: Edit Item Types */}
              <section className="border-l-4 border-amber-600 pl-6">
                <h3 className="text-xl font-semibold text-amber-600 mb-3 flex items-center gap-2">
                  <span className="bg-amber-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  Edit Item Types (Important!)
                </h3>
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-3">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-900 font-semibold mb-2">⚠️ QuickBooks Item Type Requirements</p>
                      <p className="text-gray-700 text-sm mb-2">
                        AI suggests item types, but you MUST ensure these items exist in QuickBooks:
                      </p>
                      <ol className="text-sm text-gray-700 space-y-1 ml-4">
                        <li><strong>1.</strong> Review the "QuickBooks Item Type" field for each line</li>
                        <li><strong>2.</strong> Common types: <code className="bg-gray-200 px-1 rounded">Service</code>, <code className="bg-gray-200 px-1 rounded">Non-Inventory</code>, <code className="bg-gray-200 px-1 rounded">Inventory</code></li>
                        <li><strong>3.</strong> Change to match your QuickBooks Products/Services list</li>
                        <li><strong>4.</strong> OR create matching items in QuickBooks first</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </section>

              {/* Step 4: Export */}
              <section className="border-l-4 border-green-600 pl-6">
                <h3 className="text-xl font-semibold text-green-600 mb-3 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">4</span>
                  Export QuickBooks CSV
                </h3>
                <p className="text-gray-700 mb-3">
                  Select QuickBooks as target system and download CSV format.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <ol className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-green-600">1.</span>
                      <span>In Export Options, select <strong>"QuickBooks Online"</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-green-600">2.</span>
                      <span>Click <strong>"Download QuickBooks CSV"</strong></span>
                    </li>
                  </ol>
                </div>
              </section>

              {/* Step 5: Import to QuickBooks */}
              <section className="border-l-4 border-blue-600 pl-6">
                <h3 className="text-xl font-semibold text-blue-600 mb-3 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">5</span>
                  Import to QuickBooks Online
                </h3>
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5">
                  <h4 className="font-semibold text-blue-900 mb-3">Import Steps:</h4>
                  <ol className="space-y-3 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium">Open QuickBooks Online</p>
                        <p className="text-gray-600">Log into your QuickBooks account</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium">Navigate to Invoices</p>
                        <p className="text-gray-600">Go to <strong>Sales → Invoices</strong></p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium">Click Import Button</p>
                        <p className="text-gray-600">Look for "Import" or batch import option</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                      <div>
                        <p className="font-medium">Upload Your CSV</p>
                        <p className="text-gray-600">Select the downloaded QuickBooks CSV file</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">5</span>
                      <div>
                        <p className="font-medium">Review & Confirm</p>
                        <p className="text-gray-600">Check the preview and click Import</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </section>

              {/* Pro Tips */}
              <section className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-green-600" />
                  QuickBooks Pro Tips
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-green-100">
                    <p className="font-medium text-green-900 text-sm mb-1">📦 Create Items First</p>
                    <p className="text-xs text-gray-700">Add all item types to Products/Services before import</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-100">
                    <p className="font-medium text-green-900 text-sm mb-1">✏️ Edit Item Names</p>
                    <p className="text-xs text-gray-700">Change AI suggestions to match your QB items</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-100">
                    <p className="font-medium text-green-900 text-sm mb-1">💰 Check Totals</p>
                    <p className="text-xs text-gray-700">Verify amounts match your original invoice</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-100">
                    <p className="font-medium text-green-900 text-sm mb-1">🔄 Batch Import</p>
                    <p className="text-xs text-gray-700">Process multiple invoices at once</p>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}

        {/* Coupa Instructions */}
        {instructionTab === 'coupa' && (
          <>
            {/* Legal Disclaimer Banner */}
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <p className="text-xs text-blue-900 leading-relaxed">
                <strong>⚖️ Legal Notice:</strong> This application is an independent third-party tool and is not affiliated with, endorsed by, or sponsored by Coupa Software Incorporated. "Coupa" is a registered trademark of Coupa Software Incorporated. This tool generates data files in formats compatible with Coupa's import specifications for user convenience only. All trademarks are the property of their respective owners.
              </p>
            </div>

            {/* Progress Steps for Coupa */}
            <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
              <div className="flex items-center justify-between">
                {['Upload', 'Review', 'Configure', 'Export', 'Import to Coupa'].map((step, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold mb-2">
                        {idx + 1}
                      </div>
                      <span className="text-xs font-medium text-gray-700 text-center">{step}</span>
                    </div>
                    {idx < 4 && (
                      <div className="w-12 h-0.5 bg-indigo-300 mx-2 mt-[-20px]"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <section className="border-l-4 border-indigo-600 pl-6">
                <h3 className="text-xl font-semibold text-indigo-600 mb-3 flex items-center gap-2">
                  <span className="bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  Upload Your Invoice
                </h3>
                <p className="text-gray-700 mb-3">
                  Start by uploading a PDF invoice. The system will automatically extract all relevant data using AI.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span>Click "Choose PDF File" on the Invoice Processing tab</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span>Select a valid PDF invoice from your computer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span>Wait 10-30 seconds for AI processing to complete</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Each upload uses 1 credit</strong></span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="border-l-4 border-purple-600 pl-6">
                <h3 className="text-xl font-semibold text-purple-600 mb-3 flex items-center gap-2">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Review & Edit Extracted Data
                </h3>
                <p className="text-gray-700 mb-3">
                  After processing, carefully review all extracted fields and make any necessary corrections:
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="font-semibold text-blue-900 mb-1 text-sm">📄 Invoice Header</p>
                    <p className="text-xs text-blue-800">Invoice number, date, currency, payment terms</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="font-semibold text-green-900 mb-1 text-sm">🏢 Supplier Info</p>
                    <p className="text-xs text-green-800">Supplier number, site code, addresses</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="font-semibold text-amber-900 mb-1 text-sm">📊 Line Items</p>
                    <p className="text-xs text-amber-800">Quantities, prices, descriptions, PO refs</p>
                  </div>
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                    <p className="font-semibold text-pink-900 mb-1 text-sm">💰 Charges</p>
                    <p className="text-xs text-pink-800">Shipping, handling, misc amounts</p>
                  </div>
                </div>
              </section>

              <section className="border-l-4 border-pink-600 pl-6">
                <h3 className="text-xl font-semibold text-pink-600 mb-3 flex items-center gap-2">
                  <span className="bg-pink-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  Configure GL Account (Non-PO Lines)
                </h3>
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-3">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-900 font-semibold mb-2">⚠️ Important: Non-PO Invoice Lines</p>
                      <p className="text-gray-700 text-sm mb-2">
                        Lines without a PO line number require GL account coding. Follow these steps:
                      </p>
                      <ol className="text-sm text-gray-700 space-y-1 ml-4">
                        <li><strong>1.</strong> Set your "Account Type" (Chart of Accounts)</li>
                        <li><strong>2.</strong> Enter your 20-segment GL account code</li>
                        <li><strong>3.</strong> Click "Save as Default" to reuse for future invoices</li>
                        <li><strong>4.</strong> Click "Apply to All Non-PO Lines" button</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </section>

              <section className="border-l-4 border-green-600 pl-6">
                <h3 className="text-xl font-semibold text-green-600 mb-3 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">4</span>
                  Export Your Data
                </h3>
                <p className="text-gray-700 mb-3">
                  Once all data is verified, export in your preferred format:
                </p>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                    <FileText className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                    <p className="font-semibold text-indigo-900 text-sm">JSON Format</p>
                    <p className="text-xs text-indigo-700 mt-1">API Integration</p>
                  </div>
                  <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-semibold text-green-900 text-sm">CSV Format</p>
                    <p className="text-xs text-green-700 mt-1">Manual Import</p>
                  </div>
                </div>
                <p className="text-sm text-green-700 font-medium">✅ No payment required - download freely after processing</p>
              </section>

              <section className="border-l-4 border-orange-600 pl-6">
                <h3 className="text-xl font-semibold text-orange-600 mb-3 flex items-center gap-2">
                  <span className="bg-orange-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">5</span>
                  Import to Coupa® Platform
                </h3>
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-900 mb-2">
                    <strong>📌 Disclaimer:</strong> The following instructions are provided as general guidance for importing data into Coupa®. We are not affiliated with Coupa Software Inc. Please refer to your organization's Coupa® documentation and consult your Coupa® administrator for official instructions.
                  </p>
                </div>
                <p className="text-gray-700 mb-4">
                  Follow these general steps to import your processed invoice into your Coupa® instance:
                </p>

                {/* CSV Import Method */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 mb-4">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <span className="bg-green-600 text-white px-2 py-0.5 rounded text-xs">RECOMMENDED</span>
                    Method 1: CSV Import (Manual Upload)
                  </h4>
                  <ol className="space-y-3 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium">Navigate to Coupa Invoices Load From File</p>
                        <p className="text-gray-600">Go to <strong>Invoices → Load From File</strong></p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium">Upload Your CSV File</p>
                        <p className="text-gray-600">Click "Choose File" and select your downloaded CSV</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium">Start Import</p>
                        <p className="text-gray-600">Click "Import" and wait for confirmation</p>
                      </div>
                    </li>
                  </ol>
                </div>

                {/* JSON API Method */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
                  <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                    <span className="bg-indigo-600 text-white px-2 py-0.5 rounded text-xs">ADVANCED</span>
                    Method 2: JSON API Integration
                  </h4>
                  <ol className="space-y-3 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium">API Setup</p>
                        <p className="text-gray-600">Ensure you have Coupa API credentials (OAuth key & secret)</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium">POST to Invoice API</p>
                        <p className="text-gray-600 font-mono text-xs bg-gray-100 p-2 rounded mt-1">
                          POST https://your-instance.coupahost.com/api/invoices
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium">Send JSON Payload</p>
                        <p className="text-gray-600">Use the downloaded JSON file as request body</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                      <div>
                        <p className="font-medium">Handle Response</p>
                        <p className="text-gray-600">Check for HTTP 201 (success) or error codes</p>
                      </div>
                    </li>
                  </ol>
                  <div className="mt-3 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                    <p className="text-xs text-blue-900">
                      📘<strong>Note:</strong> Refer to official Coupa® API documentation for authentication and endpoint details. API access requires proper credentials from your Coupa® administrator.
                    </p>
                  </div>
                </div>
              </section>

              <section className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                  Coupa Pro Tips & Best Practices
                </h3>
               <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <p className="font-medium text-blue-900 text-sm mb-1">⚡ Quick Review</p>
                    <p className="text-xs text-gray-700">Use "Expand All" to view all line items at once</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <p className="font-medium text-blue-900 text-sm mb-1">👀 JSON Preview</p>
                    <p className="text-xs text-gray-700">Check exact export structure before download</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <p className="font-medium text-blue-900 text-sm mb-1">💾 Save Defaults</p>
                    <p className="text-xs text-gray-700">Save GL codes to speed up future invoices</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <p className="font-medium text-blue-900 text-sm mb-1">✅ Required Fields</p>
                    <p className="text-xs text-gray-700">Fields with red asterisk (*) are mandatory</p>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}

        {/* Common Section - Free Credits (appears on both tabs) */}
        <section className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-purple-900 mb-2">Free Credits for New Users</h3>
          <p className="text-gray-700 mb-3">
            Start with <strong className="text-purple-600">10 free credits</strong> to test the system
          </p>
          <p className="text-sm text-gray-600">
            Need more? Purchase credits at just <strong>$0.10 per invoice</strong>
          </p>
        </section>
      </div>
    </div>
  );
};

export default InstructionsPage;